import { RestaurantAttendance } from '../types/restaurantPersonnel';
import { formatCivilDate, formatCivilDateTime, formatHours } from './restaurantPersonnelFormat';
import { EmployeeReportTemplate, hasRestaurantAttendanceFields, hasRestaurantPayrollSnapshotFields, hasRestaurantSalaryFields, restaurantReportFields } from './restaurantReportFields';

export interface PayrollHistoryItem {
  year: number;
  month: number;
  status: 'approved' | 'closed';
  monthlyRevenueSnapshot: number;
  totalSalaryHoursSnapshot: number;
  approvedAtUtc: string | null;
  closedAtUtc: string | null;
  employeeName: string;
  positionSnapshot: string;
  calculationMethod: string;
  workedHours: number;
  hourlyRate: number;
  evaluationCoefficient: number;
  salaryHours: number;
  baseSalary: number;
  fixedBonusSnapshot: number;
  bonusPercentSnapshot: number;
  bonus: number;
  fixedPenaltySnapshot: number;
  penaltyPercentSnapshot: number;
  penalty: number;
  fixedMonthlyRate: number;
  revenuePercent: number;
  monthlyRevenue: number;
  amount: number;
  adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
}

interface EmployeePrintInput {
  template?: EmployeeReportTemplate;
  month: string;
  employee: { fullName: string; position: string; attendance: RestaurantAttendance[] };
  canViewSalary: boolean;
  canViewBusinessRevenue: boolean;
  payrollHistory: PayrollHistoryItem[] | null;
  payrollHistoryLoading: boolean;
  payrollHistoryError: string;
  fields: string[];
}

const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character] ?? character));
const money = (value: number) => `${new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(value)} ₴`;
const decimal = (value: number) => formatHours(value, 4, false);
const row = (label: string, value: string) =>
  `<tr><th scope="row">${escapeHtml(label)}</th><td class="number">${escapeHtml(value)}</td></tr>`;
const methods: Record<string, string> = {
  'hourly-coefficient': 'Погодинна ставка × коефіцієнт',
  'fixed-plus-revenue': 'Фіксована сума + частка виторгу',
  'revenue-weighted-hours': 'Фонд за зваженими годинами',
  'halls-fund': 'Ресторанний фонд HALLS',
};

function salarySection(input: EmployeePrintInput): string {
  if (input.template === 'employee-timesheet') return '';
  const selected = new Set(input.fields);
  if (!hasRestaurantPayrollSnapshotFields(input.fields)) return '';
  if (!input.canViewSalary && !hasRestaurantSalaryFields(input.fields)) return '';
  if (!input.canViewSalary)
    return '<p class="notice">Зарплатні дані недоступні без окремого дозволу на перегляд зарплати.</p>';
  if (input.payrollHistoryError || input.payrollHistoryLoading || input.payrollHistory === null)
    return `<p class="notice">${escapeHtml(input.payrollHistoryError || (input.payrollHistoryLoading
      ? 'Історія зарплати ще завантажується. Повторіть друк після завершення завантаження.'
      : 'Історія зарплати недоступна.'))}</p>`;
  const [year, month] = input.month.split('-').map(Number);
  const period = input.payrollHistory.find(item => item.year === year && item.month === month &&
    (item.status === 'approved' || item.status === 'closed'));
  if (!period)
    return '<p class="notice">Зарплату за вибраний місяць ще не фіналізовано: затвердженого або закритого знімка немає. Поточні оцінки та інші місяці не підставляються.</p>';

  const rateParameters = !selected.has('payroll-rates') ? '' : period.calculationMethod === 'fixed-plus-revenue'
    ? row('Фіксована місячна ставка', money(period.fixedMonthlyRate)) +
      row('Частка виторгу', `${decimal(period.revenuePercent)} %`)
    : row('Ставка за годину у знімку', `${money(period.hourlyRate)} / год`);
  const businessRevenue = selected.has('payroll-revenue') && input.canViewBusinessRevenue
    ? row('Виторг ресторану у знімку', money(period.monthlyRevenue)) : '';
  const parameters = rateParameters +
    (selected.has('attendance-hours') ? row('Відпрацьовано у знімку', formatHours(period.workedHours)) +
      row('Розрахункові години у знімку', formatHours(period.salaryHours)) : '') +
    (selected.has('attendance-coefficients')
      ? row('Коефіцієнт оцінювання у знімку', decimal(period.evaluationCoefficient)) : '');
  const method = selected.has('payroll-method')
    ? `<p>${escapeHtml(methods[period.calculationMethod] ?? period.calculationMethod)}</p>` : '';
  const auditDate = (value: string) => `${formatCivilDateTime(value)} UTC`;
  const adjustments = !selected.has('adjustments') ? '' : period.adjustments.length
    ? `<table><caption>Окремі коригування після закриття</caption>
      <thead><tr><th scope="col">Дата (UTC)</th><th scope="col">Причина / автор</th><th scope="col" class="number">Сума (+/−)</th></tr></thead>
      <tbody>${period.adjustments.map(item => `<tr><td>${escapeHtml(auditDate(item.createdAtUtc))}</td>
        <td>${escapeHtml(item.reason)}<br><span class="muted">${escapeHtml(item.createdByUsername)}</span></td>
        <td class="number">${escapeHtml(money(item.amount))}</td></tr>`).join('')}</tbody></table>
      <p class="muted">Коригування наведено окремо: вони не змінюють суму первинного знімка вище.</p>`
    : '<p>Окремих коригувань після закриття немає.</p>';
  const detailed = selected.has('payroll-breakdown');
  const components = parameters + businessRevenue +
    (detailed ? row('Базове нарахування', money(period.baseSalary)) : '') +
    (selected.has('payroll-bonuses') ? row('Бонус: фіксована частина', money(period.fixedBonusSnapshot)) +
      row('Бонус: відсоток', `${decimal(period.bonusPercentSnapshot)} %`) +
      row('Бонус разом', money(period.bonus)) : '') +
    (selected.has('payroll-penalties') ? row('Штраф: фіксована частина', money(period.fixedPenaltySnapshot)) +
      row('Штраф: відсоток', `${decimal(period.penaltyPercentSnapshot)} %`) +
      row('Штраф разом (утримання)', money(period.penalty)) : '');
  const total = selected.has('payroll') ? row('Нараховано за знімком', money(period.amount)) : '';
  const adjustedTotal = selected.has('payroll') && selected.has('adjustments') && period.status === 'closed'
    ? row('Підсумок з коригуваннями',
      money(period.amount + period.adjustments.reduce((sum, item) => sum + item.amount, 0))) : '';
  const footer = total + adjustedTotal;
  return `<div class="salary-meta"><strong>${period.status === 'closed' ? 'Закритий період' : 'Затверджений період'}</strong>
    ${detailed ? `<p>Працівник у знімку: ${escapeHtml(period.employeeName)}${selected.has('position') ? ` · ${escapeHtml(period.positionSnapshot)}` : ''}</p>` : ''}
    ${method}
    ${detailed && period.approvedAtUtc ? `<p>Затверджено: ${escapeHtml(auditDate(period.approvedAtUtc))}</p>` : ''}
    ${detailed && period.closedAtUtc ? `<p>Закрито: ${escapeHtml(auditDate(period.closedAtUtc))}</p>` : ''}</div>
    ${components ? `<table class="salary"><caption>${detailed ? 'Параметри та деталізація нарахування' : 'Вибрані параметри зарплати'}</caption>
      <thead><tr><th scope="col">Складова</th><th scope="col" class="number">Значення</th></tr></thead>
      <tbody>${components}</tbody>${footer ? `<tfoot>${footer}</tfoot>` : ''}
    </table>` : footer ? `<table><tbody>${footer}</tbody></table>` : ''}
    ${detailed ? `<p class="muted">Усі суми та параметри збережені у фіналізованому знімку, без перерахунку.
      Табель нижче відображає збережені інтервали за місяць; години знімка наведені окремо.</p>` : ''}
    ${adjustments}
  `;
}

export function buildRestaurantEmployeePrintHtml(input: EmployeePrintInput): string {
  const personalTimesheet = input.template === 'employee-timesheet';
  const title = personalTimesheet ? 'Особистий місячний табель' : 'Бланк працівника';
  const [year, month] = input.month.split('-').map(Number);
  const monthLabel = new Intl.DateTimeFormat('uk-UA', { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1));
  const attendance = input.employee.attendance
    .filter(item => item.workDate.slice(0, 7) === input.month)
    .sort((a, b) => a.workDate.localeCompare(b.workDate) || a.checkInAt.localeCompare(b.checkInAt) || a.id - b.id);
  const days = new Set(attendance.map(item => item.workDate.slice(0, 10))).size;
  const completedAttendance = attendance.filter(item => item.checkOutAt);
  const openAttendance = attendance.filter(item => !item.checkOutAt);
  const openCount = openAttendance.length;
  const totalHours = completedAttendance.reduce((sum, item) => sum + item.hoursWorked, 0);
  const unfinishedHours = openAttendance.reduce((sum, item) => sum + item.hoursWorked, 0);
  const selected = new Set(input.fields.filter(field => !personalTimesheet ||
    restaurantReportFields['employee-timesheet'].allowed.includes(field)));
  const showIntervals = selected.has('attendance-intervals');
  const showHours = selected.has('attendance-hours');
  const showCoefficients = selected.has('attendance-coefficients');
  const showNotes = selected.has('attendance-notes');
  const showAttendanceRows = showIntervals || showHours || showCoefficients || showNotes;
  const attendanceColumns = 1 + (showIntervals ? 2 : 0) + Number(showHours) + Number(showCoefficients) + Number(showNotes);
  const attendanceRows = attendance.map(item => `<tr>
    <th scope="row">${escapeHtml(formatCivilDate(item.workDate))}</th>
    ${showIntervals ? `<td>${escapeHtml(formatCivilDateTime(item.checkInAt))}</td>
    <td>${escapeHtml(item.checkOutAt ? formatCivilDateTime(item.checkOutAt) : 'Не завершено')}</td>` : ''}
    ${showHours ? `<td class="number">${escapeHtml(decimal(item.hoursWorked))}${item.checkOutAt ? '' : '<br><span class="unfinished">Незавершено</span>'}</td>` : ''}
    ${showCoefficients ? `<td class="number">${escapeHtml(decimal(item.hourCoefficient))}</td>` : ''}
    ${showNotes ? `<td>${escapeHtml(item.comment)}</td>` : ''}</tr>`).join('');
  const attendanceSection = hasRestaurantAttendanceFields(Array.from(selected)) ? `<section aria-label="Табель"><h2>${personalTimesheet ? '01' : '02'} / Табель відвідування</h2>
      <p class="summary">${selected.has('attendance') ? `Днів з відмітками: <strong>${days}</strong>` : ''}
        ${showIntervals ? `· Інтервалів: <strong>${attendance.length}</strong>` : ''}
        ${selected.has('attendance-hours') ? `· Завершені години: <strong>${escapeHtml(formatHours(totalHours))}</strong>` : ''}
        ${showIntervals ? `· Незавершених інтервалів: <strong>${openCount}</strong>` : ''}
        ${openCount && showHours ? `· Незавершені години: <strong>${escapeHtml(formatHours(unfinishedHours))}</strong>` : ''}</p>
      ${selected.has('attendance') && !showAttendanceRows
        ? `<p>Робочі дати з відмітками: ${Array.from(new Set(attendance.map(item => item.workDate.slice(0, 10))))
          .map(date => escapeHtml(formatCivilDate(date))).join(', ') || 'відміток немає'}.</p>` : ''}
      ${showAttendanceRows ? `<table class="attendance"><caption>Табель за робочою датою вибраного місяця</caption>
        <thead><tr><th scope="col">Робоча дата</th>${showIntervals ? '<th scope="col">Прихід</th><th scope="col">Вихід</th>' : ''}
          ${showHours ? '<th scope="col" class="number">Години</th>' : ''}
          ${showCoefficients ? '<th scope="col" class="number">Коеф.</th>' : ''}
          ${showNotes ? '<th scope="col">Примітка</th>' : ''}</tr></thead>
        <tbody>${attendanceRows || `<tr><td colspan="${attendanceColumns}">За вибраний місяць відміток табелювання немає.</td></tr>`}</tbody>
        ${selected.has('attendance-hours') ? `<tfoot><tr><th scope="row" colspan="${showIntervals ? 3 : 1}">Разом завершених годин</th>
          <td class="number">${escapeHtml(decimal(totalHours))}</td>${showCoefficients ? '<td>—</td>' : ''}
          ${showNotes ? `<td>${openCount
            ? `Незавершено: ${showIntervals ? `${openCount} інтерв. · ` : ''}${escapeHtml(formatHours(unfinishedHours))}`
            : 'Усі інтервали завершені'}</td>` : ''}</tr></tfoot>` : ''}
      </table>` : ''}
      <p class="muted">Дні без відміток не означають прогул або вихідний. Час приходу та виходу — місцевий цивільний час.
        Години відкритих інтервалів позначені як незавершені й не входять у підсумок завершених годин.</p>
    </section>` : '';
  return `<!doctype html><html lang="uk"><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${escapeHtml(`${title} — ${input.employee.fullName} — ${input.month}`)}</title>
    <style>
      @page { size: A4 portrait; margin: 14mm 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #172b3a; background: white; font: 10pt/1.45 Arial, sans-serif; }
      header { border-top: 5px solid #244e60; border-bottom: 1px solid #244e60; padding: 14px 0; margin-bottom: 18px; }
      .eyebrow { text-transform: uppercase; letter-spacing: 1.5px; font-size: 8pt; color: #405d6b; }
      h1 { font-size: 23pt; margin: 4px 0; line-height: 1.2; }
      .identity { font-size: 15pt; font-weight: bold; margin: 12px 0 2px; overflow-wrap: anywhere; }
      .period { font-size: 12pt; margin: 4px 0; }
      h2 { font-size: 13pt; border-bottom: 2px solid #8da6b1; padding-bottom: 5px; margin: 20px 0 10px; break-after: avoid; }
      p { margin: 5px 0; overflow-wrap: anywhere; white-space: pre-wrap; }
      .muted, footer { font-size: 8.5pt; color: #435663; }
      .notice { border-left: 3px solid #8da6b1; background: #f3f6f7; padding: 10px 12px; }
      .summary { border: 1px solid #8da6b1; padding: 9px 12px; margin-bottom: 10px; }
      table { width: 100%; border-collapse: collapse; margin: 10px 0; table-layout: fixed; font-size: 9pt; }
      caption { text-align: left; font-weight: bold; padding: 5px 0; break-after: avoid; }
      th, td { border: 1px solid #b5c3ca; padding: 7px 6px; vertical-align: top; overflow-wrap: anywhere; white-space: pre-wrap; }
      th { text-align: left; font-weight: bold; }
      thead th { background: #e7eef1; }
      tbody th { font-weight: normal; }
      tbody tr:nth-child(even) { background: #f7f9fa; }
      tfoot { display: table-row-group; font-weight: bold; background: #e7eef1; }
      tfoot th, tfoot td { border-top: 2px solid #244e60; }
      .number { text-align: right; font-variant-numeric: tabular-nums; }
      .salary th:first-child { width: 65%; }
      .salary-meta { break-inside: avoid; }
      .attendance { font-size: 8.5pt; }
      footer { border-top: 1px solid #8da6b1; padding-top: 10px; margin-top: 22px; break-inside: avoid; }
      @media print {
        thead { display: table-header-group; }
        tr { break-inside: avoid; page-break-inside: avoid; }
        h1, header, .summary, .notice { break-inside: avoid; }
        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      }
    </style></head><body>
    <header><div class="eyebrow">Кадровий облік · Ресторан</div><h1>${title}</h1>
      <p class="period">Звітний місяць: <strong>${escapeHtml(monthLabel)}</strong> · ${escapeHtml(input.month)}</p>
      <p class="identity">${escapeHtml(input.employee.fullName)}</p>
      ${selected.has('position') ? `<p>${escapeHtml(input.employee.position)}</p>` : ''}
    </header>
    ${!personalTimesheet && (hasRestaurantSalaryFields(Array.from(selected)) ||
      (input.canViewSalary && hasRestaurantPayrollSnapshotFields(Array.from(selected))))
      ? `<section aria-label="Зарплата"><h2>01 / Деталізація зарплати за місяць</h2>${salarySection(input)}</section>` : ''}
    ${attendanceSection}
    <footer>Конфіденційно. Кадрові${personalTimesheet ? '' : ' та зарплатні'} відомості — лише для уповноважених осіб.
      Зберігайте цей бланк або PDF з обмеженим доступом.</footer>
    </body></html>`;
}
